import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, MessageCircle, Share2, Heart, BookmarkPlus, Flame, Play } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { useAudio } from '../context/AudioContext';
import CommentsSection from '../components/CommentsSection';
import ShareSheet from '../components/ShareSheet';
import CheckInModal from '../components/CheckInModal';
import { logMindfulSession } from '../utils/mindful';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getPostById, getCategoryName, getPostsByCategory } = useContent();
  const mockPost = getPostById(id);
  const [dbPost, setDbPost] = useState(null);
  // Try to fetch the DB record; if it exists it wins (real audio takes
  // priority over mock filler).
  const libraryRef = mockPost?.libraryRef;
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/content/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(async (data) => {
        // Built-in post backed by a library recording: borrow only the media fields.
        if (!data && libraryRef) {
          const r = await fetch(`${API_URL}/api/content/${libraryRef}`);
          const lib = r.ok ? await r.json() : null;
          if (lib) data = { _mediaOnly: true, videoUrl: lib.videoUrl, videoAssetId: lib.videoAssetId, audioUrl: lib.audioUrl, audioAssetId: lib.audioAssetId, coverImageUrl: lib.coverImageUrl, durationSeconds: lib.durationSeconds };
        }
        if (!cancelled) setDbPost(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, libraryRef]);
  const post = dbPost
    ? {
        ...mockPost,
        id: dbPost._mediaOnly ? mockPost.id : dbPost.id,
        title: dbPost._mediaOnly ? mockPost.title : dbPost.title,
        excerpt: dbPost.shortDescription || mockPost?.excerpt,
        content: dbPost.description || mockPost?.content || dbPost.shortDescription || '',
        date: mockPost?.date || (dbPost.publishedAt || dbPost.createdAt ? new Date(dbPost.publishedAt || dbPost.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''),
        comments: mockPost?.comments || 0,
        tags: dbPost.tags || mockPost?.tags || [],
        image: dbPost.coverImageUrl || mockPost?.image,
        category: dbPost.category || mockPost?.category,
        audioAssetId: dbPost.audioAssetId,
        audioUrl: dbPost.audioUrl,
        videoUrl: dbPost.videoAssetId ? `${API_URL}/api/media/${dbPost.videoAssetId}` : (dbPost.videoUrl || null),
        duration: dbPost.durationSeconds ? `${Math.max(1, Math.round(dbPost.durationSeconds / 60))} min` : mockPost?.duration,
      }
    : mockPost;
  const { play: playAudio, state: audioState } = useAudio();
  const {
    user, likedPosts, bookmarkedPosts, readPosts, streak,
    toggleLikePost, toggleBookmarkPost, markPostRead
  } = useAuth();

  const isLiked = likedPosts.includes(id);
  const isBookmarked = bookmarkedPosts.includes(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const visitStartRef = useRef(Date.now());
  const programId = searchParams.get('program');
  const sessionId = searchParams.get('session');

  // Meditation posts get a deep forest-green wrapper with soft-white text, so
  // the reader visually knows they've entered a listening space. Sleep posts
  // shift into the midnight blue palette used on the Sleep category shell.
  const isMeditation = post && (post.category === 'guided-meditations' || post.categoryId === 'guided-meditations');
  const isSleep = post && (post.category === 'sleep' || post.categoryId === 'sleep');

  // Scroll to top only when the post id changes (not when user/profile updates)
  useEffect(() => {
    window.scrollTo(0, 0);
    visitStartRef.current = Date.now();
    setCheckInOpen(false);
  }, [id]);

  // For meditations & sleep posts, prompt a gentle mood check-in after
  // ~60 seconds of dwell time — long enough that they've actually settled
  // into the reading/audio, short enough that we catch them before they
  // navigate away.
  useEffect(() => {
    if (!isMeditation && !isSleep) return;
    const t = setTimeout(() => setCheckInOpen(true), 60_000);
    return () => clearTimeout(t);
  }, [id, isMeditation, isSleep]);

  // On unmount or post change, log a mindful session if user lingered, and
  // complete the related program session if this view was opened from a program.
  useEffect(() => {
    const programIdSnapshot = programId;
    const sessionIdSnapshot = sessionId;
    return () => {
      const dur = Math.round((Date.now() - visitStartRef.current) / 1000);
      if (dur >= 30 && post) {
        const kind = post.category === 'sleep' ? 'sleep' : 'meditation';
        logMindfulSession({ kind, durationSec: Math.min(dur, 3600), ref: id });
        // If launched from a program session, mark the session complete now.
        if (programIdSnapshot && sessionIdSnapshot) {
          const token = localStorage.getItem('rbr_token');
          if (token) {
            // Fire-and-forget; no need to await on unmount.
            fetch(
              `${API_URL}/api/programs/${programIdSnapshot}/sessions/${sessionIdSnapshot}/complete`,
              { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
            ).catch((e) => console.warn('program complete failed', e));
          }
        }
      }
    };
  }, [id, post, programId, sessionId]);

  // Mark post as read once per post per session, after user is available
  useEffect(() => {
    if (user && post && !readPosts.includes(id)) {
      markPostRead(id);
    }
  }, [id, user, post, readPosts, markPostRead]);

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-5">
        <p className="text-stone-500 text-lg mb-4">Post not found</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 btn-gold rounded-full text-sm font-medium transition-colors duration-300"
        >
          Go Home
        </button>
      </div>
    );
  }

  const relatedPosts = getPostsByCategory(post.category)
    .filter((p) => p.id !== post.id)
    .slice(0, 3);

  const contentParagraphs = String(post.content || '').split('\n\n').filter(Boolean);

  const handleLike = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    toggleLikePost(id);
  };

  const handleBookmark = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    toggleBookmarkPost(id);
  };

  // Meta Info - reader style below computes from isMeditation / isSleep declared above
  const readerStyle = isSleep
    ? { background: 'linear-gradient(180deg, #152638 0%, #1a2f47 55%, #152638 100%)', color: '#E4DEF0', minHeight: '100vh' }
    : isMeditation
      ? { background: 'linear-gradient(180deg, #173F35 0%, #245549 55%, #173F35 100%)', color: '#F8F6F0', minHeight: '100vh' }
      : undefined;

  return (
    <div className="animate-fade-in" style={readerStyle} data-testid={isMeditation ? 'meditation-reader' : isSleep ? 'sleep-reader' : 'post-reader'}>
      {/* Hero Image */}
      <div className="relative">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-72 object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: isSleep
              ? 'linear-gradient(to top, #152638 0%, rgba(21,38,56,0.35) 55%, transparent 100%)'
              : isMeditation
                ? 'linear-gradient(to top, #173F35 0%, rgba(23,63,53,0.35) 55%, transparent 100%)'
                : 'linear-gradient(to top, #F7F3EA 0%, rgba(0,0,0,0.30) 55%, transparent 100%)',
          }}
        />

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 bg-black/40 backdrop-blur-md border border-[#D6AD60]/25 rounded-full flex items-center justify-center hover:bg-black/60 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5 text-stone-800" />
        </button>

        {/* Actions */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={handleBookmark}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              isBookmarked
                ? 'bg-[#D6AD60] text-[#1a1814]'
                : 'bg-black/40 backdrop-blur-md border border-[#D6AD60]/25 text-stone-800 hover:bg-black/60'
            }`}
          >
            <BookmarkPlus className={`w-5 h-5 ${isBookmarked ? 'fill-[#1a1814]' : ''}`} />
          </button>
          <button
            onClick={() => setShareOpen(true)}
            className="w-10 h-10 bg-black/40 backdrop-blur-md border border-[#D6AD60]/25 rounded-full flex items-center justify-center hover:bg-black/60 transition-colors duration-300"
            data-testid="post-share-btn"
            aria-label="Share reflection"
          >
            <Share2 className="w-5 h-5 text-stone-800" />
          </button>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <span className="text-xs font-medium text-[#D6AD60] uppercase tracking-wider mb-1.5 block">
            {getCategoryName(post.category)}
          </span>
          <h1
            className="text-2xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {post.title}
          </h1>
        </div>
      </div>

      {/* Reader Paper — light reading surface on dark backdrop */}
      <div className="reader-paper mx-3 mt-[-1.25rem] rounded-3xl shadow-2xl relative z-10 overflow-hidden">
      {/* Meta Info */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-stone-200/60">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-xs text-stone-500">
            <Calendar className="w-3.5 h-3.5" />
            {post.date}
          </span>
          {post.comments > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-stone-500">
              <MessageCircle className="w-3.5 h-3.5" />
              {post.comments} {post.comments === 1 ? 'Comment' : 'Comments'}
            </span>
          )}
        </div>
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
            isLiked
              ? 'bg-red-50 text-red-500'
              : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
          }`}
        >
          <Heart
            className={`w-3.5 h-3.5 ${isLiked ? 'fill-red-500' : ''}`}
          />
          {isLiked ? 'Liked' : 'Like'}
        </button>
      </div>

      {/* Streak Nudge */}
      {user && streak.current > 0 && (
        <div className="mx-5 mt-4 px-4 py-3 bg-orange-50 rounded-2xl flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Flame className="w-4 h-4 text-orange-500" />
          </div>
          <div>
            <p className="text-xs font-semibold text-orange-700">
              {streak.current} day streak!
            </p>
            <p className="text-[10px] text-orange-400">
              Keep reading to maintain your streak
            </p>
          </div>
        </div>
      )}

      {/* Sign up nudge for non-logged users */}
      {!user && (
        <div className="mx-5 mt-4 px-4 py-3 bg-stone-100 rounded-2xl flex items-center gap-3">
          <div className="w-8 h-8 bg-stone-200 rounded-xl flex items-center justify-center flex-shrink-0">
            <Flame className="w-4 h-4 text-stone-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-stone-600">
              Track your reflections
            </p>
            <p className="text-[10px] text-stone-400">
              Create a profile to earn streaks & badges
            </p>
          </div>
          <Link
            to="/auth"
            className="text-xs font-semibold text-stone-700 bg-white px-3 py-1.5 rounded-xl hover:bg-stone-50 transition-colors duration-300 flex-shrink-0"
          >
            Join
          </Link>
        </div>
      )}

      {/* Real audio player — when the DB attaches an audioAssetId or audioUrl */}
      {(post.audioAssetId || post.audioUrl) && (
        <div className="mx-5 mb-4 p-4 rounded-2xl" data-testid="post-audio-play-card" style={{ background: 'linear-gradient(135deg, #173F35 0%, #0D2B24 100%)', color: '#F6F1E7' }}>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1" style={{ color: '#C79A4A' }}>
            Listen — Rowdy&rsquo;s voice
          </p>
          <p className="text-lg mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>{post.title}</p>
          <button
            type="button"
            onClick={() => {
              const src = post.audioUrl || (post.audioAssetId ? `${API_URL}/api/media/${post.audioAssetId}` : null);
              if (!src) return;
              playAudio({
                src,
                title: post.title,
                subtitle: 'Rowdy Solomon',
                artwork: post.image,
                kind: post.category === 'sleep' ? 'sleep' : 'reflection',
                contentId: post.id,
                sourceContext: programId ? 'program' : 'library',
              });
            }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
            style={{ background: '#C79A4A', color: '#0D2B24' }}
            data-testid="post-audio-play-btn"
          >
            <Play className="w-4 h-4" />
            {audioState.current?.contentId === post.id && audioState.playing ? 'Now playing' : 'Play'}
          </button>
        </div>
      )}

      {/* Rowdy's recorded session (served from our own media library) */}
      {post.videoUrl && (
        <div className="px-5 mt-6" data-testid="post-video-section">
          <h3 className="font-serif text-lg text-stone-800 mb-3">Guided Session</h3>
          <video
            controls
            playsInline
            preload="metadata"
            poster={post.image || undefined}
            src={post.videoUrl}
            className="w-full rounded-2xl bg-black shadow-lg"
            data-testid="post-video-player"
          />
        </div>
      )}

      {/* Content */}
      <div className="px-5 py-6">
        <div className="post-content">
          {contentParagraphs.map((paragraph, index) => (
            <p
              key={`${index}-${paragraph.slice(0, 24)}`}
              className="text-stone-700 text-[15px] leading-relaxed mb-5 last:mb-0"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>
      </div>
      {/* /Reader Paper */}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="px-5 pb-8 pt-8">
          <h3
            className="text-base font-semibold text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            More in {getCategoryName(post.category)}
          </h3>
          <div className="flex flex-col gap-3">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.id}
                to={`/post/${relatedPost.id}`}
                className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 group"
              >
                <img
                  src={relatedPost.image}
                  alt={relatedPost.title}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0 group-hover:scale-105 transition-transform duration-500"
                />
                <div className="min-w-0">
                  <h4
                    className="text-sm font-semibold text-stone-800 line-clamp-1 group-hover:text-[#D6AD60] transition-colors duration-300"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {relatedPost.title}
                  </h4>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {relatedPost.date}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="px-5 pb-8">
        <CommentsSection postId={post.id} />
      </div>

      <ShareSheet open={shareOpen} onClose={() => setShareOpen(false)} post={post} />

      <CheckInModal
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        contentRef={`post:${id}`}
        title={isSleep ? 'How are you feeling before sleep?' : 'How are you feeling after that meditation?'}
      />
    </div>
  );
};

export default PostDetailPage;
